import * as THREE from 'three';
import { unzipSync } from 'three/examples/jsm/libs/fflate.module.js';

const ROOT_MODEL = '3D/3dmodel.model';

const children = (node, name) => Array.from(node.children).filter((child) => child.localName === name);

const child = (node, name) => children(node, name)[0];

const getAttr = (node, name) => {
  const attr = Array.from(node.attributes).find((a) => a.localName === name);
  return attr ? attr.value : null;
};

const normalizePath = (path) => path.replace(/^\//, '');

/**
 * Parse a 3MF transform attribute into a Matrix4
 */
const parseTransform = (value) => {
  const matrix = new THREE.Matrix4();
  if (!value) return matrix;

  const t = value.trim().split(/\s+/).map(parseFloat);
  matrix.set(
    t[0], t[3], t[6], t[9],
    t[1], t[4], t[7], t[10],
    t[2], t[5], t[8], t[11],
    0, 0, 0, 1
  );
  return matrix;
};

/**
 * Build a BufferGeometry from a <mesh> node
 */
const parseMesh = (meshNode) => {
  const vertices = [];
  for (const v of children(child(meshNode, 'vertices'), 'vertex')) {
    vertices.push(parseFloat(v.getAttribute('x')), parseFloat(v.getAttribute('y')), parseFloat(v.getAttribute('z')));
  }

  const indices = [];
  for (const t of children(child(meshNode, 'triangles'), 'triangle')) {
    indices.push(parseInt(t.getAttribute('v1'), 10), parseInt(t.getAttribute('v2'), 10), parseInt(t.getAttribute('v3'), 10));
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
};

/**
 * Parse a 3MF file (supports the production extension used by slicers)
 */
export const parse3MF = (buffer, material) => {
  const zip = unzipSync(new Uint8Array(buffer));
  const decoder = new TextDecoder();
  const documents = {};

  const loadDocument = (path) => {
    if (!documents[path]) {
      if (!zip[path]) throw new Error(`Missing 3MF part: ${path}`);
      const xml = new DOMParser().parseFromString(decoder.decode(zip[path]), 'application/xml');
      const objects = {};
      for (const node of xml.getElementsByTagNameNS('*', 'object')) {
        objects[node.getAttribute('id')] = node;
      }
      documents[path] = { xml, objects };
    }
    return documents[path];
  };

  const getObject = (path, id) => {
    const node = loadDocument(path).objects[id];
    if (!node) throw new Error(`Missing 3MF object: ${path}#${id}`);
    return node;
  };

  const buildObject = (path, id) => {
    const node = getObject(path, id);
    const group = new THREE.Group();

    const meshNode = child(node, 'mesh');
    if (meshNode) group.add(new THREE.Mesh(parseMesh(meshNode), material));

    const componentsNode = child(node, 'components');
    if (componentsNode) {
      for (const component of children(componentsNode, 'component')) {
        const componentPath = getAttr(component, 'path');
        const object = buildObject(componentPath ? normalizePath(componentPath) : path, component.getAttribute('objectid'));
        object.applyMatrix4(parseTransform(component.getAttribute('transform')));
        group.add(object);
      }
    }

    return group;
  };

  // Find the root model from the package relationships
  const rels = zip['_rels/.rels'] ? decoder.decode(zip['_rels/.rels']) : '';
  const rootMatch = rels.match(/Target="([^"]+\.model)"/i);
  const rootPath = rootMatch ? normalizePath(rootMatch[1]) : ROOT_MODEL;

  const result = new THREE.Group();
  const build = loadDocument(rootPath).xml.getElementsByTagNameNS('*', 'build')[0];

  for (const item of children(build, 'item')) {
    const itemPath = getAttr(item, 'path');
    const object = buildObject(itemPath ? normalizePath(itemPath) : rootPath, item.getAttribute('objectid'));
    object.applyMatrix4(parseTransform(item.getAttribute('transform')));
    result.add(object);
  }

  return result;
};
